import type { ReactNode } from "react";
import type { ErrorCode } from "../lib/http/errors";
import { FE_META, feText } from "../i18n/fe";
import type { Audience } from "../i18n/fe";
import { useT } from "../i18n";
import { FeBanner } from "./FeBanner";
import type { BannerAction } from "./FeBanner";
import { useFocusOnMount } from "../hooks/useFocusOnMount";

/** FE 코드 → FS-C1 하위 화면 ID(SCR v0.6) — data-testid 는 화면 코드 소문자(ScreenRoot 규칙) */
export function c1ScreenOf(code: ErrorCode): string {
  switch (code) {
    case "FE-304":
      return "FS-C1-02";
    case "FE-301":
    case "FE-302":
      return "FS-C1-03";
    case "FE-601":
      return "FS-C1-04";
    case "FE-303":
      return "FS-C1-05";
    case "FE-202":
    case "FE-203":
      return "FS-C1-07";
    default:
      return "FS-C1";
  }
}

/**
 * FS-C1 전체 화면 상태 — 막다른 화면 금지: 항상 '계속할 수 있는 것'(입구·캠퍼스·다시)을 함께 말한다.
 * 표시되면 배너로 포커스를 옮긴다(스크린 리더가 바로 읽음).
 */
export function StatusScreen({
  code,
  audience,
  actions,
  note,
}: {
  code: ErrorCode;
  audience: Audience;
  actions?: readonly BannerAction[];
  /** 배너 아래 덧붙임(예: Bench 에서 돌아왔는데 세션이 없을 때 '캠퍼스에서 다시 열어 주세요') */
  note?: ReactNode;
}) {
  const t = useT();
  const ref = useFocusOnMount<HTMLDivElement>(code);
  // 학생에게 숨기는 코드라도 전체 화면이 비면 안 되므로 교사 문구 대신 세션 안내로 대체
  const shown: ErrorCode = audience === "student" && FE_META[code].studentSilent ? "FE-304" : code;
  return (
    <section
      className="pf-pane pf-pane--hero pf-status"
      aria-labelledby="pf-status-title"
      data-testid={c1ScreenOf(shown).toLowerCase()}
      data-screen={c1ScreenOf(shown)}
    >
      <h1 id="pf-status-title" className="pf-sr-only">
        {feText(t, shown, audience).cause}
      </h1>
      <FeBanner ref={ref} code={shown} audience={audience} actions={actions}>
        {shown === "FE-601" && (
          <ul className="pf-chips" aria-label={t("device.supported")}>
            {t("device.supported")
              .split(" · ")
              .map((b) => (
                <li key={b} className="pf-chip-static">
                  {b}
                </li>
              ))}
          </ul>
        )}
        {note}
      </FeBanner>
    </section>
  );
}
